import { Badge, Box, Button, CircularProgress, Drawer, FormControl, IconButton, InputAdornment, TextField, Theme, Tooltip, Typography, useTheme } from "@mui/material";
import { CommonLayout } from "../commonComponents";
import { SearchIcon } from "../customSVGs";
import { useEffect, useState } from "react";
import { DialogCloseIcon, ExportIcon, FilterIcon, InfoIconOutlined } from "../../assets/SvgIcons";
import CustomTable from "../investorOverview/common/customTable";
import { Order, PaginationObj, SortingObj, TableHeaderData } from "../investorOverview/types";
import OrdersApprovalTableBody from "./ordersApprovalTableBody";
import CustomFilter from "../investorOverview/common/CustomFilter";
import { ModeOfHoldingTypeFilter, OrderHistoryRes, OrderListObj, ProductTypeFilter, ResidentStatusTypeFilter } from "../../redux-store/types/orderManagement";
import { getApprovalTypeFilter, getModeOfHoldingFilter, getProductTypeFilter, getResidentStatusFilter } from "../../redux-store/actions/orderManagement";
import { useDispatch } from "react-redux";
import { useDebounce } from "../../utils/useDebounce";
import { getOrderApprovalList, getOrderApprovalExcelReport } from "../../redux-store/actions/orderApproval";
import { ApprovalStatuFilter } from "../../redux-store/types/orderApproval";
import { whiteSpaceOnlyRegex } from "../../utils/regex";
import { reportDownload } from "../Reports/report-utils";
import { showError } from "../../redux-store/actions/auth";
import { EXPORT_TOOLTIP_TEXT } from "../../utils/constant";


const tableHeaderData: TableHeaderData[] = [
    {
        id: 'orderNumber',
        label: 'ORDER NUMBER',
        align: 'center',
        isSortable: true
    },
    {
        id: 'orderDate',
        label: 'ORDER DATE',
        align: 'center',
        isSortable: true
    },
    {
        id: 'clientName',
        label: 'INVESTOR NAME',
        align: 'center',
        isSortable: true
    },
    {
        id: 'clientCode',
        label: 'CLIENT CODE',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'investorType',
        label: 'INVESTOR TYPE',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'modeOfHolding',
        label: 'MODE OF HOLDING',
        align: 'center',
        isSortable: false,
    },
    {
        id: 'productType',
        label: 'PRODUCT',
        align: 'center',
        isSortable: true
    },
    {
        id: 'portfolioName',
        label: 'ACCOUNT',
        align: 'center',
        isSortable: true
    },
    {
        id: 'accountType',
        label: 'ACCOUNT TYPE',
        align: 'center',
        isSortable: true
    },
    {
        id: 'paymentStatus',
        label: 'PAYMENT STATUS',
        align: 'center',
        isSortable: true
    },
    {
        id: 'approvalStatus',
        label: 'ORDER STATUS',
        align: 'center',
        isSortable: true
    },
]



export default function OrdersApproval(): JSX.Element {
    const { palette } = useTheme();
    const dispatch = useDispatch();
    const [productFilterData, setProductFilterData] = useState<ProductTypeFilter[]>([])
    const [approvalFilterData, setApprovalFilterData] = useState<ApprovalStatuFilter[]>([])
    const [residentStatusFilterData, setResidentStatusFilterData] = useState<Array<ResidentStatusTypeFilter>>([])
    const [modeOfHoldingFilterData, setModeOfHoldingFilterData] = useState<Array<ModeOfHoldingTypeFilter>>([])
    const [searchText, setSearchText] = useState<string>('');
    const [orderApprovalLoading, setOrderApprovalLoading] = useState<boolean>(false);
    const [isFiltersLoading, setIsFiltersLoading] = useState<boolean>(false);
    const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
    const [tableSortingObj, setTableSortingObj] = useState<SortingObj>({
        order: "desc",
        orderBy: ""
    })
    const [tablePaginationObj, setTablePaginationObj] = useState<PaginationObj>({
        currentPage: 0,
        rowsPerPage: 10,
        totalRows: 0
    })
    const [totalFilters, setTotalFilters] = useState<number>(0);
    const [tableData, setTableData] = useState<OrderListObj[]>([]);

    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false)
    const debounceSearchText = useDebounce(searchText, 500);



    const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map((obj: ProductTypeFilter) => `${obj.orderStatusId}`)

    const approvalFilterPayload = approvalFilterData?.filter(obj => obj.checked)?.map((obj: ApprovalStatuFilter) => `${obj.orderStatusId}`)

    const residentStatusFilterPayload = residentStatusFilterData?.filter(obj => obj.checked)?.map((obj: ResidentStatusTypeFilter) => `${obj.orderStatusId}`)

    const modeOfHoldingFilterPayload = modeOfHoldingFilterData?.filter(obj => obj.checked)?.map((obj: ModeOfHoldingTypeFilter) => `${obj.orderStatusId}`)

    const fetchOrderApprovalList = async ({
        sort = "",
        order = "",
        productType = [],
        approvalStatus = [],
        investorType = [],
        modeOfHolding = [],
        searchInput = "",
        currentPage = 1,
        rowsPerPage = 10,
    }: any) => {
        try {
            setOrderApprovalLoading(true);

            const payload = {
                productType,
                approvalStatus,
                investorType,
                modeOfHolding,
                searchInput,
                sort,
                order,
                currentPage,
                rowsPerPage
            }

            const orderApprovalListRes = await (dispatch(getOrderApprovalList(payload)) as unknown as Promise<OrderHistoryRes>)
            const { orders, totalCount } = orderApprovalListRes;

            setTableData(orders);
            setTablePaginationObj(prevState => ({
                ...prevState,
                totalRows: totalCount
            }))
        } catch (error) {
            console.log('error', error);
        } finally {
            setOrderApprovalLoading(false);
        }
    }

    const handleExport = async () => {
        try {
            setIsExportLoading(true);
            const payload = {
                productType: productFilterPayload,
                approvalStatus: approvalFilterPayload,
                investorType: residentStatusFilterPayload,
                modeOfHolding: modeOfHoldingFilterPayload,
                searchInput: searchText.trim(), // NOTE: intentionally uses searchText (not debounceSearchText) — export is a point-in-time action triggered by a button click and should reflect the user's exact current input, not the 500ms-debounced value used for table fetches.
                sort: tableSortingObj?.orderBy,
                order: tableSortingObj?.order || "desc",
                currentPage: 1,
                rowsPerPage: tablePaginationObj?.rowsPerPage, // uses the same rowsPerPage as the table
            };
            const blob = await (dispatch(getOrderApprovalExcelReport(payload)) as unknown as Promise<Blob>);
            reportDownload(blob, 'OrderApprovals.xlsx');
        } catch (error) {
            console.error('export error', error);
            dispatch(showError('Failed to export. Please try again.'));
        } finally {
            setIsExportLoading(false);
        }
    }

    const handleOpenFilterDrawer = () => {
        setFilterDrawerOpen(true)
    }

    const handleCloseFilterDrawer = () => {
        setFilterDrawerOpen(false)
    }

    const handleApplyFilters = () => {
        setFilterDrawerOpen(false)

        const productFilterPayload = productFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId)

        const approvalFilterPayload = approvalFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId);

        const residentStatusFilterPayload = residentStatusFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId)

        const modeOfHoldingFilterPayload = modeOfHoldingFilterData?.filter(obj => obj.checked)?.map(obj => obj.orderStatusId)

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        const totalAppliedFilters =
            (productFilterPayload?.length ?? 0) +
            (approvalFilterPayload?.length ?? 0) +
            (residentStatusFilterPayload?.length ?? 0) +
            (modeOfHoldingFilterPayload?.length ?? 0)

        setTotalFilters(totalAppliedFilters)


        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const isFilterApplied = (
        productFilterData?.some(obj => obj.checked) ||
        approvalFilterData?.some(obj => obj.checked) ||
        residentStatusFilterData?.some(obj => obj.checked) ||
        modeOfHoldingFilterData?.some(obj => obj.checked)
    )

    const handleFiltersClearAll = () => {
        setProductFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setApprovalFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })));
        setResidentStatusFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))
        setModeOfHoldingFilterData(prevState => prevState?.map(item => ({ ...item, checked: false })))

        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))

        setTotalFilters(0)

        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            searchInput: debounceSearchText?.trim(),
            currentPage: 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage
        })

        setFilterDrawerOpen(false)
    }

    const handleProductTypeFilterChange = (value: string, checked: boolean) => {
        setProductFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleApprovalTypeFilterChange = (value: string, checked: boolean) => {
        setApprovalFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleResidentStatusTypeFilterChange = (value: string, checked: boolean) => {
        setResidentStatusFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleModeOfHoldingTypeFilterChange = (value: string, checked: boolean) => {
        setModeOfHoldingFilterData(prevState => prevState?.map(
            item => item.orderStatusId === value ? { ...item, checked } : item)
        )
    }

    const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (whiteSpaceOnlyRegex.test(event.target.value)) return;
        setSearchText(event.target.value);
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
    }

    const handleClearSearch = (e: React.MouseEvent<HTMLButtonElement>) => {
        setSearchText("")
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: 0
        }))
        e.preventDefault()
    }

    const handleSort = async (orderBy: string, order: Order) => {

        setTableSortingObj({
            order,
            orderBy
        })

        fetchOrderApprovalList({
            sort: orderBy,
            order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText?.trim(),
            currentPage: tablePaginationObj?.currentPage + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            rowsPerPage: Number(event.target.value),
            currentPage: 0,
        }))
        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            rowsPerPage: Number(event.target.value),
        })
    }

    const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
        setTablePaginationObj(prevState => ({
            ...prevState,
            currentPage: page
        }))

        fetchOrderApprovalList({
            sort: tableSortingObj?.orderBy,
            order: tableSortingObj?.order,
            productType: productFilterPayload,
            approvalStatus: approvalFilterPayload,
            investorType: residentStatusFilterPayload,
            modeOfHolding: modeOfHoldingFilterPayload,
            searchInput: debounceSearchText.trim(),
            currentPage: page + 1,
            rowsPerPage: tablePaginationObj?.rowsPerPage,
        })
    }

    useEffect(() => {
        async function fetchData() {
            if (
                productFilterData?.length === 0 ||
                approvalFilterData?.length === 0 ||
                residentStatusFilterData?.length === 0 ||
                modeOfHoldingFilterData?.length === 0
            ) {

                try {
                    setIsFiltersLoading(true);
                    const productFilterRes = await (dispatch(getProductTypeFilter()) as unknown as Promise<ProductTypeFilter[]>)
                    setProductFilterData((productFilterRes ?? []).filter(obj => `${obj?.orderStatusId ?? ""}`.toLowerCase() !== "fee payment"));

                    const approvalFilterRes = await (dispatch(getApprovalTypeFilter()) as unknown as Promise<ApprovalStatuFilter[]>)
                    setApprovalFilterData(approvalFilterRes?.map(obj => {
                        if (obj?.orderStatusId?.toLowerCase() === "pending") {
                            return { ...obj, checked: true }
                        }
                        return obj
                    }));

                    const residentStatusFilterRes = await (dispatch(getResidentStatusFilter()) as unknown as Promise<Array<ResidentStatusTypeFilter>>)
                    setResidentStatusFilterData(residentStatusFilterRes)

                    const modeOfHoldingFilterRes = await (dispatch(getModeOfHoldingFilter()) as unknown as Promise<Array<ModeOfHoldingTypeFilter>>)
                    setModeOfHoldingFilterData(modeOfHoldingFilterRes)

                    await fetchOrderApprovalList({
                        sort: tableSortingObj?.orderBy,
                        order: tableSortingObj?.order,
                        productType: productFilterRes
                            ?.filter(obj => obj.checked)
                            ?.map(obj => obj?.orderStatusId),
                        approvalStatus: approvalFilterRes
                            ?.map(obj => {
                                if (obj?.orderStatusId?.toLowerCase() === "pending") {
                                    return { ...obj, checked: true }
                                }
                                return obj
                            })
                            ?.filter(obj => obj?.checked)
                            ?.map(obj => obj?.orderStatusId),
                        investorType: residentStatusFilterRes
                            ?.filter(obj => obj.checked)
                            ?.map(obj => obj?.orderStatusId),
                        modeOfHolding: modeOfHoldingFilterRes
                            ?.filter(obj => obj.checked)
                            ?.map(obj => obj?.orderStatusId),
                        searchInput: debounceSearchText.trim(),
                        currentPage: 1,
                        rowsPerPage: tablePaginationObj?.rowsPerPage,
                    })

                    setTotalFilters(1);
                } catch (e) {
                    console.log('error', e);
                }
                finally {
                    setIsFiltersLoading(false);
                }
                return
            }

            await fetchOrderApprovalList({
                sort: tableSortingObj?.orderBy,
                order: tableSortingObj?.order,
                productType: productFilterPayload,
                approvalStatus: approvalFilterPayload,
                investorType: residentStatusFilterPayload,
                modeOfHolding: modeOfHoldingFilterPayload,
                searchInput: debounceSearchText.trim(),
                currentPage: 1,
                rowsPerPage: tablePaginationObj?.rowsPerPage,
            })
        }

        fetchData()
    }, [debounceSearchText])

    return (
        // <CommonLayoutForPOA>
        <CommonLayout>
            <>
                <Box>
                    <Typography sx={{ color: 'text.primary', mb: 2.5 }} variant='h1'>
                        Order Approvals
                    </Typography>

                    <Box sx={{ background: palette.background.paper, borderRadius: '25px', overflow: 'hidden', padding: '30px 25px' }}>
                        <Box sx={{ mb: '35px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', }}>
                            <FormControl variant="standard" sx={{ display: 'block' }}>
                                <TextField
                                    id="outlined-multiline-flexible"
                                    value={searchText}
                                    placeholder={`Search  Order`}

                                    onChange={handleSearchTextChange}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="Clear Search"
                                                    sx={{ display: searchText.length > 0 ? 'inline-flex' : 'none' }}
                                                    onMouseDown={handleClearSearch}
                                                >
                                                    <DialogCloseIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                        style: {
                                            borderRadius: '5px',
                                            height: '40px',
                                            width: '100%',
                                            maxWidth: '255px',
                                            fontSize: '16px',
                                        },
                                    }}
                                />
                            </FormControl>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                {/* Export */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Box
                                        component={'button'}
                                        onClick={handleExport}
                                        disabled={isExportLoading}
                                        sx={{ cursor: isExportLoading ? 'not-allowed' : 'pointer', border: 'none', display: 'flex', gap: '8px', background: 'transparent', alignItems: 'center', opacity: isExportLoading ? 0.6 : 1 }}
                                    >
                                        {isExportLoading
                                            ? <CircularProgress size={16} sx={{ color: palette.text.primary }} />
                                            : <ExportIcon sx={{ color: palette.text.primary }} />
                                        }
                                        <Typography variant="body_500" sx={{ color: palette.text.primary }}>Export</Typography>
                                    </Box>
                                    <Tooltip
                                        title={EXPORT_TOOLTIP_TEXT}
                                        placement="top"
                                        arrow
                                    >
                                        <IconButton size="small" sx={{ padding: '2px' }} aria-label="Export info">
                                            <InfoIconOutlined />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                {/* Filters */}
                                <Button
                                    variant="text"
                                    startIcon={
                                        <Badge
                                            showZero
                                            badgeContent={totalFilters}
                                            sx={(theme: Theme) => ({
                                                '& .MuiBadge-badge': {
                                                    right: 0,
                                                    top: -3,
                                                    backgroundColor: theme.palette.primary[500],
                                                    color: theme.palette.common.white,
                                                    ...theme.typography.subtitle_600
                                                },
                                            })}
                                        >
                                            <FilterIcon style={{ opacity: 1 }} />
                                        </Badge>
                                    }
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary,
                                        padding: "5px",
                                        ...theme.typography.body_500,
                                        "&:hover": {
                                            backgroundColor: theme.palette.grey[50]
                                        }
                                    })}
                                    TouchRippleProps={{
                                        style: {
                                            color: palette.grey[300]
                                        }
                                    }}
                                    onClick={handleOpenFilterDrawer}
                                    disabled={false}
                                >
                                    Filters
                                </Button>
                            </Box>
                        </Box>
                        <CustomTable
                            isLoading={orderApprovalLoading || isFiltersLoading}
                            tableBody={
                                <OrdersApprovalTableBody data={tableData ?? []} />
                            }
                            tableData={tableData ?? []}
                            tableHeaderData={tableHeaderData}
                            isPaginationVisible={true}
                            tableDataCount={tablePaginationObj?.totalRows ?? 0}
                            handleSort={handleSort}
                            order={tableSortingObj.order}
                            orderBy={tableSortingObj.orderBy}
                            handleRowsPerPageChange={handleRowsPerPageChange}
                            handlePageChange={handlePageChange}
                            currentPage={tablePaginationObj.currentPage}
                            rowsPerPageOptions={[5, 10, 20, 30]}
                            rowsPerPage={tablePaginationObj.rowsPerPage}
                            tableBodyLoaderRowCustomStyles={{
                                '& td:first-of-type': {
                                    paddingLeft: "0px"
                                },
                            }}
                            tableHeadRowCustomStyles={{
                                '& th:first-of-type': {
                                    paddingLeft: "0px"
                                }
                            }}
                            tableBodyCustomStyles={{
                                '& tr:last-child td': {
                                    border: 0
                                }
                            }}
                            loadingRowsNum={5}
                        />
                    </Box>
                </Box>
                <Drawer
                    open={isFilterDrawerOpen}
                    onClose={handleCloseFilterDrawer}
                    anchor="right"
                    PaperProps={{
                        sx: {
                            padding: "25px",
                            width: "450px"
                        }
                    }}
                    ModalProps={{
                        sx: (theme: Theme) => ({
                            backgroundColor: `${theme.palette.common.black}03`
                        })
                    }}
                >
                    <CustomFilter
                        isLoading={isFiltersLoading}
                        handleClearAll={handleFiltersClearAll}
                        handleApplyFilters={handleApplyFilters}
                        showCloseIcon={true}
                        handleCloseIconClick={handleCloseFilterDrawer}
                        showAmcFilter={false}
                        showCategoryFilter={false}
                        showRiskFilter={false}
                        showProductTypeFilter={true}
                        showPaymentTypeFilter={false}
                        showApprovalTypeFilter={true}
                        showTransactionTypeFilter={false}
                        showResidentStatusTypeFilter={true}
                        showModeOfHoldingTypeFilter={true}
                        productTypeFilterData={productFilterData}
                        approvalTypeFilterData={approvalFilterData}
                        residentStatusFilterData={residentStatusFilterData}
                        modeOfHoldingFilterData={modeOfHoldingFilterData}
                        handleProductTypeFilterChange={handleProductTypeFilterChange}
                        handleApprovalTypeFilterChange={handleApprovalTypeFilterChange}
                        handleResidentStatusTypeFilterChange={handleResidentStatusTypeFilterChange}
                        handleModeOfHoldingTypeFilterChange={handleModeOfHoldingTypeFilterChange}
                        isFilterApplied={isFilterApplied}
                        defaultExpandedFilter="productType"
                    />
                </Drawer>
            </>
        </CommonLayout>
        // </CommonLayoutForPOA>
    )
}
